import { Fragment, ReactFragment, useEffect, useState } from 'react'
import moment from 'moment'
import { connect } from 'react-redux'
import Activities from '@components/Activities/Activities'
import NewsItem from '@/app/containers/NewsItem'
import News from '@components/News/News'
import CalendarActivities from '@components/CalendarActivities/CalendarActivities'
import { Analytics, commentsActions, CommentType, layoutActions, ModalsType } from '@Techedge/digitalportal-shared'
import { LuxIcon } from '@lux/wardrobe-lib'
import { setHideSidebar, setShowContent } from '../redux/actions/activities'
import {
    getCalendarActivities,
    resetCalendarActivities,
    setCalendarActivitiesFilters,
} from '../redux/actions/calendar-activities'
import { getMostUrgentActivities } from '../redux/actions/most-urgent-activities'
import { getNews, setActiveTab, setFilters } from '../redux/actions/news'
import { ActivitiesFiltersProps, ActivityProps } from '../models/activities'
import { NewsTabBarId } from '../models/news'
import { formatDate } from '../utils/commonFunctions'
import './Home.scss'
import { ActivityTypeModalsMap } from '../utils/activityTypeModals.map'
import { localStorageBreadcrumbKey } from '@utils/export-utils'

const Home = (props: {
    user
    accessToken
    newsDetails
    showNewsDetails
    getCalendarActivities
    getMostUrgentActivities
    getNews
    showContent
    hideSidebar
    setHideSidebar
    setActiveTab
    calendarActivities
    calendarActivitiesLoading
    calendarActivitiesFilters
    mostUrgentActivities
    mostUrgentActivitiesLoading
    news
    newsLoading
    newsFilters
    totalNews
    activeTab
    tabBarConfiguration
    openActivity
    modalType
    setShowContent
    setType
    userLanguage
    setFilters
    setCalendarActivitiesFilters
    totalCalendarActivities
    resetCalendarActivities
}) => {
    const {
        user,
        accessToken,
        newsDetails,
        showNewsDetails,
        getCalendarActivities,
        getMostUrgentActivities,
        getNews,
        showContent,
        hideSidebar,
        setHideSidebar,
        setActiveTab,
        calendarActivities,
        calendarActivitiesLoading,
        calendarActivitiesFilters,
        mostUrgentActivities,
        mostUrgentActivitiesLoading,
        news,
        newsLoading,
        newsFilters,
        totalNews,
        activeTab,
        tabBarConfiguration,
        openActivity,
        modalType,
        setType,
        setShowContent,
        userLanguage,
        setFilters,
        setCalendarActivitiesFilters,
        totalCalendarActivities,
        resetCalendarActivities,
    } = props

    const homeClass = 'smart-home'

    const params = new URLSearchParams(window.location.search)
    const mode = params.get('mode')

    const sidebarCloseClass = `${homeClass}__sidebar--close`
    const sidebarHideClass = `${homeClass}__sidebar--hide`
    const chevronHideClass = `${homeClass}__chevron--hide`

    const [currentDate, setCurrentDate] = useState(new Date())
    const [calendarHeight, setCalendarHeight] = useState<number>(0)

    const [filteredCalendarActivities, setFilteredCalendarActivities] = useState<ActivityProps[]>(
        []
    )

    const onCloseSidebar = () => {
        setHideSidebar(!hideSidebar)
    }

    const tabBarHandler = (tabId: NewsTabBarId): void => {
        setActiveTab(tabId)
        let tmpFilters: ActivitiesFiltersProps = {
            ...newsFilters,
            page: 1,
            page_size: 10,
        }
        switch (tabId) {
            case NewsTabBarId.CURRENT_WEEK:
                tmpFilters = {
                    ...tmpFilters,
                    start_date: {
                        value: `${formatDate(
                            moment().startOf('isoWeek').toString(),
                            'YYYY-MM-DD'
                        )} 00:00`,
                        orderBy: 'DESC',
                    },
                    end_date: {
                        value: `${formatDate(
                            moment().endOf('isoWeek').toString(),
                            'YYYY-MM-DD'
                        )} 23:59`
                    },
                    useIntervalDate: true,
                }
                break
            case NewsTabBarId.CURRENT_MONTH:
                tmpFilters = {
                    ...tmpFilters,
                    start_date: {
                        value: `${formatDate(
                            moment().startOf('month').toString(),
                            'YYYY-MM-DD'
                        )} 00:00`,
                        orderBy: 'DESC',
                    },
                    end_date: {
                        value: `${formatDate(
                            moment().endOf('month').toString(),
                            'YYYY-MM-DD'
                        )} 23:59`
                    },
                    useIntervalDate: true,
                }
                break
            case NewsTabBarId.LAST_WEEK:
                tmpFilters = {
                    ...tmpFilters,
                    start_date: {
                        value: `${formatDate(
                            moment().startOf('isoWeek').subtract(1, 'days').startOf('isoWeek').toString(),
                            'YYYY-MM-DD'
                        )} 00:00`,
                        orderBy: 'DESC',
                    },
                    end_date: {
                        value: `${formatDate(
                            moment().startOf('isoWeek').subtract(1, 'days').endOf('isoWeek').toString(),
                            'YYYY-MM-DD'
                        )} 00:00`
                    },
                    useIntervalDate: true,
                }
                break
            default:
                tmpFilters = {
                    ...tmpFilters,
                    start_date: null,
                    end_date: null,
                    useIntervalDate: false,
                }
        }
        setFilters(tmpFilters)
        getNews(tmpFilters)
    }

    useEffect(() => {
        setType(CommentType.PROJECT)
        return () => {
            setType(null)
        }
    }, [])

    useEffect(() => {
        if (!mode && !!user && !!user.user_id) {
            Analytics._sendAnalyticsLinkEvent('Home', '', 'Static')
        }
    }, [user])

    useEffect(() => {
        if (accessToken && !modalType) {
            getMostUrgentActivities()
            tabBarHandler(NewsTabBarId.CURRENT_WEEK) // getNews()
        }
    }, [accessToken, modalType])

    useEffect(() => {
        if (accessToken && !modalType && currentDate && !calendarActivitiesLoading) {
            resetCalendarActivities()
            setCalendarActivitiesFilters({ page: 1 })
            getCalendarActivities(currentDate)
        }
    }, [accessToken, modalType, currentDate])

    useEffect(() => {
        let newCalendarActivities = []
        if (calendarActivities && mostUrgentActivities) {
            let tmpCalendarActivities = [...calendarActivities]
            let tmpMostUrgentActivities = [...mostUrgentActivities]
            let mostUrgentActivitiesIds =
                tmpMostUrgentActivities?.length > 0
                    ? tmpMostUrgentActivities.map((a: ActivityProps) => {
                          return a.id
                      })
                    : []
            if (tmpCalendarActivities?.length > 0) {
                for (let i = 0; i < tmpCalendarActivities.length; i++) {
                    let a = tmpCalendarActivities[i]
                    let check = false
                    for (let x = 0; x < mostUrgentActivitiesIds.length; x++) {
                        let id = mostUrgentActivitiesIds[x]
                        if (a.id === id) {
                            check = true
                        }
                    }
                    if (!check) {
                        newCalendarActivities.push(a)
                    }
                }
            }
        }
        setFilteredCalendarActivities(newCalendarActivities)
    }, [calendarActivities, mostUrgentActivities])

    let newsDetailsContent: ReactFragment
    if (!!newsDetails)
        newsDetailsContent = (
            <Fragment>
                <NewsItem
                    news={newsDetails}
                    commentsList={newsDetails.commentsList}
                    reactions={newsDetails.reactions}
                    isDetailsMode={true}></NewsItem>
            </Fragment>
        )

    const getMoreNews = (isBottom: boolean) => {
        if (isBottom && !newsLoading && news.length < totalNews) {
            let tmpFilters: ActivitiesFiltersProps = {
                ...newsFilters,
                page: newsFilters.page + 1,
            }
            getNews(tmpFilters, true)
        }
    }

    const onScrollCalendarActivities = (event) => {
        if (
            Math.floor(event.target.scrollHeight - event.target.scrollTop) -
                Math.floor(event.target.clientHeight) <=
                1 &&
            calendarActivities.length < totalCalendarActivities &&
            !calendarActivitiesLoading
        ) {
            setCalendarActivitiesFilters({ page: calendarActivitiesFilters.page + 1 })
            getCalendarActivities(currentDate)
        }
    }

    // onScroll Desktop
    const onScroll = (event) => {
        setShowContent(!event.currentTarget.scrollTop)
        getMoreNews(
            Math.floor(event.target.scrollHeight - event.target.scrollTop) -
                Math.floor(event.target.clientHeight) <=
                1
        )
    }

    // onScroll Mobile
    const [scrollTop, setScrollTop] = useState<number>(0)
    useEffect(() => {
        const handleScroll = (event) => {
            setScrollTop(window.scrollY)
        }
        window.addEventListener('scroll', handleScroll)
        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [])
    useEffect(() => {
        if (scrollTop) {
            let element = document.getElementsByClassName(`${homeClass}__content`)[0] as HTMLElement
            getMoreNews(
                !!element &&
                    Math.floor(element?.offsetHeight - scrollTop) -
                        Math.floor(window.innerHeight) <=
                        1
            )
        }
    }, [scrollTop])

    return (
        <div className={`${homeClass}__container`}>
            {!showNewsDetails && (
                <div className={`${homeClass}__body`}>
                    <div className={`${homeClass}__sidebar ${hideSidebar ? sidebarHideClass : ''}`}>
                        <CalendarActivities
                            activities={filteredCalendarActivities}
                            loading={calendarActivitiesLoading}
                            openActivity={openActivity}
                            currentDate={currentDate}
                            setCurrentDate={setCurrentDate}
                            calendarHeight={calendarHeight}
                            setCalendarHeight={setCalendarHeight}
                            userLanguage={userLanguage}
                            onScroll={onScrollCalendarActivities}
                            totalCalendarActivities={totalCalendarActivities}
                        />
                    </div>
                    <div
                        className={`${homeClass}__chevron ${hideSidebar ? chevronHideClass : ''}`}
                        style={{
                            marginTop: calendarHeight,
                        }}>
                        <div
                            onClick={onCloseSidebar}
                            className={`${sidebarCloseClass} ${hideSidebar ? 'rotate' : ''}`}>
                            <LuxIcon icon="caret-left" />
                        </div>
                    </div>
                    <div
                        onScroll={onScroll}
                        className={`${homeClass}__content ${
                            hideSidebar ? `${homeClass}__content--full` : ''
                        }`}>
                        <Activities
                            activities={mostUrgentActivities}
                            loading={mostUrgentActivitiesLoading}
                            showContent={showContent}
                            hideSidebar={hideSidebar}
                            openActivity={openActivity}
                        />
                        <News
                            newsList={news}
                            loading={newsLoading}
                            activities={mostUrgentActivities}
                            activeTab={activeTab}
                            tabBarConfiguration={tabBarConfiguration}
                            showContent={showContent}
                            hideSidebar={hideSidebar}
                            tabBarHandler={tabBarHandler}
                        />
                    </div>
                </div>
            )}
            {showNewsDetails && <div className={`${homeClass}__body`}>{newsDetailsContent}</div>}
        </div>
    )
}

const mapStateToProps = (state: any) => ({
    user: state.userReducer.user,
    newsDetails: state.newsReducer.newsDetails,
    accessToken: state.userReducer.accessToken,
    showNewsDetails: state.newsReducer.showNewsDetails,
    showContent: state.activitiesReducer.showContent,
    hideSidebar: state.activitiesReducer.hideSidebar,
    calendarActivities: state.calendarActivitiesReducer.activities,
    totalCalendarActivities: state.calendarActivitiesReducer.totalCalendarActivities,
    calendarActivitiesLoading: state.calendarActivitiesReducer.loading,
    calendarActivitiesFilters: state.calendarActivitiesReducer.filters,
    mostUrgentActivities: state.mostUrgentActivitiesReducer.activities,
    mostUrgentActivitiesLoading: state.mostUrgentActivitiesReducer.loading,
    news: state.newsReducer.newsList,
    newsLoading: state.newsReducer.loading,
    newsFilters: state.newsReducer.filters,
    totalNews: state.newsReducer.totalNews,
    activeTab: state.newsReducer.activeTab,
    tabBarConfiguration: state.newsReducer.tabBarConfiguration,
    modalType: state.layoutReducer.modals.type,
    userLanguage: state.userReducer.user.language,
})

const mapDispatchToProps = (dispatch: any) => ({
    getCalendarActivities: (selectedDate?: Date) => dispatch(getCalendarActivities(selectedDate)),
    resetCalendarActivities: () => dispatch(resetCalendarActivities()),
    getMostUrgentActivities: () => dispatch(getMostUrgentActivities()),
    getNews: (customFilters?: ActivitiesFiltersProps, moreNews?: boolean) =>
        dispatch(getNews(customFilters, moreNews)),
    setHideSidebar: (hideSidebar: boolean) => dispatch(setHideSidebar(hideSidebar)),
    setActiveTab: (activeTab: NewsTabBarId) => dispatch(setActiveTab(activeTab)),
    setShowContent: (showContent: boolean) => dispatch(setShowContent(showContent)),
    openActivity: (a) => {
        localStorage.setItem(
            localStorageBreadcrumbKey,
            window.location.href
        )
        dispatch(
            layoutActions.setModals({
                type: ActivityTypeModalsMap[a.type] || ModalsType.TASK_EXECUTION,
                props: { id: a.id, storeIds: a.store_ids },
            })
        )
    },
    setType: (type: CommentType) => dispatch(commentsActions.setType(type)),
    setFilters: (filters: ActivitiesFiltersProps) => dispatch(setFilters(filters)),
    setCalendarActivitiesFilters: (filters: ActivitiesFiltersProps) =>
        dispatch(setCalendarActivitiesFilters(filters)),
})

export default connect(mapStateToProps, mapDispatchToProps)(Home)
